import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Input, Icon } from '../../../../../components';

const CreateSpaceInputContainer = styled.div`
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
`;

const LinkButton = styled.button`
  display: inline-flex;

  font-family: var(--font-family-title);
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;

  color: var(--primary-default);

  &:hover {
    color: var(--primary-hover);
  }

  &:active {
    color: var(--primary-pressed);
  }
`;

const StyledInput = styled(Input)`
  color: ${({ $error }) => ($error ? 'var(--critical-default)' : 'var(--content-body)')};
  @media (max-width: 768px) {
    font-size: 13px;

    &::placeholder {
      font-size: 13px;
    }
  }
`;

const CreateSpaceForm = ({ className = null, spaces = [], onSubmit }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = useCallback(() => {
    onSubmit(value);
    setValue('');
  }, [onSubmit, value]);

  const handleKeyDown = useCallback(
    (evt) => {
      if (evt.key === 'Enter' && !error) {
        handleSubmit();
      }
    },
    [handleSubmit, error]
  );

  const iconColor = useMemo(() => {
    if (error) {
      return 'var(--critical-default)';
    }
    return value === '' ? 'var(--content-subtitle)' : 'var(--content-body)';
  }, [value, error]);

  useEffect(() => {
    if (spaces.some((s) => s.title.toLowerCase().trim() === value.toLowerCase().trim())) {
      setError(true);
    } else if (error) {
      setError(false);
    }
  }, [spaces, value, error]);

  return (
    <CreateSpaceInputContainer className={className}>
      <Icon name='plus' size={20} displaySize={16} color={iconColor} />
      <StyledInput
        placeholder='Create a new space'
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        value={value}
        $error={error}
      />
      {value !== '' && !error && <LinkButton onClick={handleSubmit}>Create</LinkButton>}
    </CreateSpaceInputContainer>
  );
};

CreateSpaceForm.propTypes = {
  className: PropTypes.string,
  spaces: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string
    })
  ),
  onSubmit: PropTypes.func.isRequired
};

export { CreateSpaceForm };
